import {
  Typography,
  Grid,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useAppTheme } from "core/contexts/theme/theme";
import { formatDate } from "../../home";
import { useEffect, useState } from "react";
import { getApiKeys } from "modules/agentMySecurity/services/embeddingsService";
import { CustomPieText } from "../dashboard.styled";
import { CustomTooltip } from "../../home";
import { fontWeight } from "styled-system";
import AdminApiKey from "./apiKeys";
import { RotatedTick } from "core/components/global/tables/rechart/helpers";

interface ApiKey {
  _id: string;
  description: string;
  groups: string[];
  active: boolean;
  created_by: string;
  createdAt: string;
}

// API Usage Tab
export const ApiUsageTab = () => {
  const { apptheme } = useAppTheme();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loadingApiKeys, setIsLoadingApiKeys] = useState(true);

  const fetchApiKeys = async () => {
    const keys = await getApiKeys();
    return keys;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get the list of conversations
        const keys: ApiKey[] = await fetchApiKeys();
        setApiKeys(keys);

        // Reset the loading
        setIsLoadingApiKeys(false);
      } catch (error) {
        // Reset the loading
        setIsLoadingApiKeys(false);
      }
    };

    fetchData();
  }, []);

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <CustomPieText
        x={x}
        y={y}
        fill={apptheme.colors.textColor.slice(1)}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </CustomPieText>
    );
  };

  return (
    <Grid container spacing={3}>
      {/* API Key Status */}
      <Grid item xs={12} md={3}>
        <Paper className="p-2">
          {loadingApiKeys || apiKeys === undefined || apiKeys === null ? (
            <Skeleton variant="rectangular" height={300} animation="wave" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={[
                    {
                      name: "Active",
                      value: apiKeys.filter((k: any) => k.active).length,
                    },
                    {
                      name: "Inactive",
                      value: apiKeys.filter((k: any) => !k.active).length,
                    },
                  ]}
                  labelLine={false}
                  outerRadius={90}
                  dataKey="value"
                  stroke={apptheme.colors.textColorOpposite}
                  strokeWidth={5}
                  label={renderCustomizedLabel}
                >
                  <Cell fill="#4caf50" />
                  <Cell fill="#f44336" />
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </Grid>

      {/* API Usage Trend */}
      <Grid item xs={12} md={9}>
        <Paper className="p-2">
          {loadingApiKeys ? (
            <Skeleton variant="rectangular" height={300} animation="wave" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={getDailyKeyCounts(apiKeys)}
                margin={{ top: 40, right: 20, left: 20, bottom: 20 }}
              >
                <text
                  x={"1rem"}
                  y={"1rem"}
                  textAnchor="start"
                  dominantBaseline="middle"
                  style={{
                    fontSize: apptheme.font.large,
                    fill: apptheme.colors.textColor,
                    fontWeight: "600"
                  }}
                >
                  API Key Creation Trend
                </text>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis interval={0} dataKey="date" tick={<RotatedTick fill={apptheme.colors.textColor} />} />
                <YAxis label={{ fill: apptheme.colors.textColor }} />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: apptheme.colors.barChartBarFullWBgHv }}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke={apptheme.colors.barChartBarBg}
                  name="API Calls"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </Grid>

      {/* API Keys by Group */}
      <Grid item xs={12}>
        <Paper className="p-2">
          {loadingApiKeys ? (
            <Skeleton variant="rectangular" height={500} animation="wave" />
          ) : (
            <ResponsiveContainer width="100%" height={500}>
              <BarChart
                data={summarizeByGroup(apiKeys)}
                margin={{ top: 40, right: 20, left: 20, bottom: 200 }}
              >
                <text
                  x={"1rem"}
                  y={"2rem"}
                  textAnchor="start"
                  dominantBaseline="middle"
                  style={{
                    fontSize: apptheme.font.large,
                    fill: apptheme.colors.textColor,
                    fontWeight: "600"
                  }}
                >
                  API Keys by Group
                </text>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis interval={0} dataKey="group" tick={<RotatedTick />} />
                <YAxis label={{ fill: apptheme.colors.textColor }} />
                <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: apptheme.colors.barChartBarFullWBgHv }}
                />
                <Legend
                  verticalAlign="top"
                  align="right"
                  layout="horizontal"
                  wrapperStyle={{ 
                    top: "1rem", right: "1rem"
                  }}
                />
                <Bar
                  dataKey="active"
                  stackId="a"
                  name="Active Keys"
                  fill={apptheme.colors.barChartBarBg}
                  radius={[0, 4, 4, 0]}
                />
                <Bar
                  dataKey="inactive"
                  stackId="a"
                  name="Inactive Keys"
                  fill={apptheme.colors.highlight3}
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </Grid>

      {/* API Keys Table */}
      <Grid item xs={12}>
        <AdminApiKey/>
      </Grid>
    </Grid>
  );
};

type GroupSummary = {
  group: string;
  active: number;
  inactive: number;
};

function summarizeByGroup(items: ApiKey[]): GroupSummary[] {
  const groupMap = new Map<string, { active: number; inactive: number }>();

  for (const item of items) {
    for (const group of item.groups) {
      if (!groupMap.has(group)) {
        groupMap.set(group, { active: 0, inactive: 0 });
      }

      const groupStats = groupMap.get(group)!;
      if (item.active) {
        groupStats.active += 1;
      } else {
        groupStats.inactive += 1;
      }
    }
  }

  const response = Array.from(groupMap.entries()).map(([group, counts]) => ({
    group,
    ...counts,
  }));
  return response;
}

function getDailyKeyCounts(data: { createdAt: string }[]) {
    const result: { date: string; count: number }[] = [];
    const today = new Date();
  
    for (let i = 30; i >= 0; i--) {
      const date = new Date();
      date.setDate(today.getDate() - i);
      const dateKey = date.toISOString().slice(0, 10);
  
      const count = data.filter((item) => {
        const itemDate = new Date(item.createdAt).toISOString().slice(0, 10);
        return itemDate === dateKey;
      }).length;
  
      result.push({
        date: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        count,
      });
    }
  
    return result;
  }
